import React from 'react';
import { RadioInput, Checkbox } from '@ibinder/ui-common-core';

import {
  PrintType,
  TableHeaderPropName,
  TableHeaderType,
  TableDataType,
  GeneralPrintProps,
  AllDataObjectTypeRow,
  DataObjectType
} from './GeneralPrint.types';
import {
  SettingsOption,
  FormatGroupType
} from '../../../../types/common.types';
import { RadioInputContainer } from './GeneralPrint.styles';

export const getTableHeaders = (
  type: PrintType,
  action?: (propName: TableHeaderPropName, id: string, value?: any) => void
): TableHeaderType[] => {
  const defaultHeaders: TableHeaderType[] = [
    {
      id: '3',
      name: 'Description',
      propName: 'description',
      Cell: ({ cell }) => cell.value
    },
    {
      id: '4',
      name: 'Enabled',
      propName: 'enabled',
      Cell: ({ cell }) => (
        <Checkbox
          disabled={cell.row.values[5]}
          label=""
          checked={cell.value}
          onChange={() => action('enabled', cell.row.values[0], !cell.value)}
        />
      )
    },
    {
      id: '5',
      name: 'Default',
      propName: 'default',
      Cell: ({ cell }) => (
        <RadioInputContainer>
          <RadioInput
            id="default"
            onClick={() => action('default', cell.row.values[0])}
            selectedKey={cell.value}
            data={[
              {
                Key: true,
                Value: '',
                disabled: !Object.values(cell.row.values).at(-3)
              }
            ]}
          />
        </RadioInputContainer>
      )
    }
  ];

  if (type === 'formatGroups') {
    return [
      {
        id: '0',
        name: 'ID',
        propName: 'id',
        Cell: ({ cell }) => cell.value
      },
      {
        id: '1',
        name: 'Name',
        propName: 'name',
        Cell: ({ cell }) => cell.value
      },
      {
        id: '2',
        name: 'Associated formats',
        propName: 'associatedFormats',
        Cell: ({ cell }) => cell.value
      },
      ...defaultHeaders
    ];
  }

  return [
    {
      id: '0',
      name: 'ID',
      propName: 'id',
      Cell: ({ cell }) => cell.value
    },
    {
      id: '1',
      name: 'Name',
      propName: 'name',
      Cell: ({ cell }) => cell.value
    },
    ...defaultHeaders
  ];
};

export const getTableData = (
  data: DataObjectType,
  headers: TableHeaderType[],
  allData: AllDataObjectTypeRow[]
): TableDataType[] => {
  const getValueByPropName = (
    row: AllDataObjectTypeRow,
    propName: TableHeaderPropName
  ): string | boolean => {
    switch (propName) {
      case 'id':
      case 'name':
      case 'description':
      case 'associatedFormats':
        return row[propName];
      case 'enabled':
        return data?.rows?.length
          ? data?.rows?.includes(row.id)
          : row.id === data.default;
      case 'default':
        return row.id === data.default;
      default:
        return '';
    }
  };

  return allData?.map((row) => ({
    metadata: headers.map((header) => ({
      metadataElementId: header.id,
      value: getValueByPropName(row, header.propName),
      sortValue: getValueByPropName(row, header.propName)
    }))
  }));
};

export const getAllData = (
  type: PrintType,
  allOptions: SettingsOption[] | FormatGroupType[]
): AllDataObjectTypeRow[] => {
  if (type === 'formatGroups') {
    return (allOptions as FormatGroupType[])?.map(
      ({ id, name, description, printFormats }) => ({
        id,
        name,
        description,
        associatedFormats: printFormats?.reduce(
          (acc, pf, idx) => acc + `${idx !== 0 ? ', ' : ''}` + pf.id,
          ''
        )
      })
    );
  }

  return allOptions?.map(({ id, name, description }) => ({
    id,
    name,
    description
  }));
};

export const convertToDefault = (type: PrintType): string => {
  return `default${type[0].toUpperCase()}${type.slice(1, -1)}`;
};
